import Fuse from 'fuse.js';
import type { InventoryItem, InventorySnapshot, InventoryVariation, StockByLocation } from '../shared/types';

export type StockFilter = 'all' | 'in' | 'low' | 'out' | 'stale';
export type StockSort = 'name-asc' | 'name-desc' | 'stock-asc' | 'stock-desc' | 'updated-asc' | 'updated-desc';

export function variationStock(variation: InventoryVariation, locationId: string | null): number {
  return locationId ? Math.max(0, variation.stockByLocation[locationId] ?? 0) : Math.max(0, variation.totalStock);
}

export function itemStock(item: InventoryItem, locationId: string | null): number {
  if (!locationId) return Math.max(0, item.totalStock);
  return item.variations.reduce((sum, variation) => sum + variationStock(variation, locationId), 0);
}

export function isStale(item: InventoryItem, now = Date.now()): boolean {
  const value = item.lastStockChange ?? item.updatedAt;
  if (!value) return true;
  const timestamp = Date.parse(value);
  return !Number.isFinite(timestamp) || now - timestamp >= 14 * 24 * 60 * 60 * 1_000;
}

export function makeFuse(items: InventoryItem[]): Fuse<InventoryItem> {
  return new Fuse(items, {
    keys: ['name', 'description', 'categoryName', 'variations.name', 'variations.sku'],
    threshold: 0.32,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });
}

export function selectItems(
  items: InventoryItem[],
  fuse: Fuse<InventoryItem>,
  search: string,
  filter: StockFilter,
  sort: StockSort,
  locationId: string | null,
  now = Date.now(),
): InventoryItem[] {
  const term = search.trim();
  const source = term ? fuse.search(term).map((result) => result.item) : items;
  const filtered = source.filter((item) => {
    const stock = itemStock(item, locationId);
    if (filter === 'in') return stock > 0;
    if (filter === 'low') return stock >= 1 && stock <= 5;
    if (filter === 'out') return stock === 0;
    if (filter === 'stale') return isStale(item, now);
    return true;
  });
  return filtered.toSorted((a, b) => {
    if (sort === 'name-asc') return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    if (sort === 'name-desc') return b.name.localeCompare(a.name, undefined, { sensitivity: 'base' });
    if (sort === 'stock-asc') return itemStock(a, locationId) - itemStock(b, locationId);
    if (sort === 'stock-desc') return itemStock(b, locationId) - itemStock(a, locationId);
    const aTime = Date.parse(a.lastStockChange ?? a.updatedAt ?? '') || 0;
    const bTime = Date.parse(b.lastStockChange ?? b.updatedAt ?? '') || 0;
    return sort === 'updated-asc' ? aTime - bTime : bTime - aTime;
  });
}

export function inventoryStats(items: InventoryItem[], locationId: string | null) {
  return items.reduce((stats, item) => {
    const stock = itemStock(item, locationId);
    stats.stock += stock;
    if (stock === 0) stats.out += 1;
    else if (stock <= 5) stats.low += 1;
    return stats;
  }, { products: items.length, stock: 0, low: 0, out: 0 });
}

export function patchVariationStock(
  snapshot: InventorySnapshot,
  variationId: string,
  locationId: string,
  update: (current: number) => number,
): InventorySnapshot {
  return {
    ...snapshot,
    items: snapshot.items.map((item) => {
      if (!item.variations.some((variation) => variation.id === variationId)) return item;
      const variations = item.variations.map((variation) => {
        if (variation.id !== variationId) return variation;
        const previous = variation.stockByLocation[locationId] ?? 0;
        const next = Math.max(0, Math.trunc(update(previous)));
        const stockByLocation: StockByLocation = { ...variation.stockByLocation, [locationId]: next };
        return {
          ...variation,
          stockByLocation,
          totalStock: Object.values(stockByLocation).reduce((sum, quantity) => sum + Math.max(0, quantity), 0),
          lastStockChange: new Date().toISOString(),
        };
      });
      return {
        ...item,
        variations,
        totalStock: variations.reduce((sum, variation) => sum + variation.totalStock, 0),
        lastStockChange: new Date().toISOString(),
      };
    }),
  };
}

export function safeName(value: string, fallback = 'Unnamed product'): string {
  const cleaned = Array.from(value).filter((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint > 31 && codePoint !== 127;
  }).join('').trim();
  return cleaned || fallback;
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function formatRelativeTime(value: string | null, now = Date.now()): string {
  if (!value) return 'No stock changes yet';
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Update time unavailable';
  const seconds = Math.round((timestamp - now) / 1_000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return formatter.format(days, 'day');
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(timestamp);
}

export function clampedDelta(current: number, requested: number): number {
  return Math.max(0, current + requested) - current;
}
